import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import './RescheduledRequest.css';

const RescheduledRequestList = ({ requests, onViewRequest, onRefresh, currentUserId }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();

  const filterRequests = (requests, filter) => {
    switch (filter) {
      case 'pending':
        return requests.filter(req => req.status === 'pending');
      case 'approved':
        return requests.filter(req => req.status === 'approved');
      case 'rejected':
        return requests.filter(req => req.status === 'rejected');
      default:
        return requests;
    }
  };

  const filteredRequests = filterRequests(requests, activeFilter);

  const getStatusBadgeClass = status => {
    switch (status) {
      case 'pending':
        return 'reschedule-status-badge-pending';
      case 'approved':
        return 'reschedule-status-badge-approved';
      case 'rejected':
        return 'reschedule-status-badge-rejected';
      default:
        return 'reschedule-status-badge-pending';
    }
  };

  const formatStatus = status => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleOpenDetail = request => {
    if (onViewRequest) onViewRequest(request);
    navigate(`/instructor/account/reschedule-request-detail/${request.id}`, { state: { request } });
  };

  const getRequestByMessage = request => {
    if (!currentUserId || !request.requestedBy || !request.requestedTo) {
      return 'Reschedule Request';
    }

    if (request.requestedBy === currentUserId) {
      return 'Reschedule Request by You';
    } else {
      return 'Reschedule Request by Learner';
    }
  };

  return (
    <div className="reschedule-list-wrapper">
      <div className="reschedule-sticky-header">
        <div className="reschedule-list-header">
          <h1 className="reschedule-list-title">Reschedule Details</h1>
          {onRefresh && (
            <button onClick={onRefresh} className="reschedule-refresh-btn" title="Refresh requests">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
              </svg>
            </button>
          )}
        </div>

        <div className="reschedule-filter-tabs">
          <button
            className={`reschedule-filter-tab ${activeFilter === 'all' ? 'reschedule-filter-tab-active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All
          </button>
          <button
            className={`reschedule-filter-tab ${activeFilter === 'pending' ? 'reschedule-filter-tab-active' : ''}`}
            onClick={() => setActiveFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`reschedule-filter-tab ${activeFilter === 'approved' ? 'reschedule-filter-tab-active' : ''}`}
            onClick={() => setActiveFilter('approved')}
          >
            Approved
          </button>
          <button
            className={`reschedule-filter-tab ${activeFilter === 'rejected' ? 'reschedule-filter-tab-active' : ''}`}
            onClick={() => setActiveFilter('rejected')}
          >
            Rejected
          </button>
        </div>
      </div>

      <div className="reschedule-list-content">
        {filteredRequests.length === 0 ? (
          <div className="reschedule-empty-state">
            <div className="reschedule-empty-icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  ry="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <h3 className="reschedule-empty-title">No reschedule requests found</h3>
            <p className="reschedule-empty-message">
              {activeFilter === 'all'
                ? "You don't have any reschedule requests at the moment."
                : `No ${activeFilter} reschedule requests found.`}
            </p>
            {onRefresh && (
              <button onClick={onRefresh} className="reschedule-empty-refresh-btn">
                Refresh
              </button>
            )}
          </div>
        ) : (
          filteredRequests.map(request => (
            <div
              key={request.id}
              className="reschedule-request-card"
              onClick={() => handleOpenDetail(request)}
            >
              <div className="reschedule-card-header">
                <div className="reschedule-student-info">
                  <img
                    src={request.studentAvatar || '/placeholder.svg'}
                    alt={request.studentName}
                    className="reschedule-student-avatar"
                  />
                  <div className="reschedule-student-details">
                    <div className="reschedule-student-name-row">
                      <span className="reschedule-student-name">{request.studentName}</span>
                      <span className="reschedule-student-country">
                        {request.country} <ReactCountryFlag countryCode={request.countryCode} svg />
                      </span>
                    </div>
                    <div className="reschedule-session-datetime">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="reschedule-calendar-icon"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                        <line
                          x1="3"
                          y1="10"
                          x2="21"
                          y2="10"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span>{request.rescheduleDate}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="reschedule-clock-icon"
                      >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" />
                      </svg>
                      <span>{request.rescheduleTime}</span>
                    </div>
                  </div>
                </div>
                <span className={`reschedule-status-badge ${getStatusBadgeClass(request.status)}`}>
                  {formatStatus(request.status)}
                </span>
              </div>

              <div className="reschedule-card-body">
                <div className="reschedule-subject-info">
                  <span className="reschedule-subject-label">Subject:</span>
                  <span className="reschedule-subject-value">{request.subject}</span>
                </div>
                <div className="reschedule-session-info">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="reschedule-user-icon"
                  >
                    <path
                      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="reschedule-session-type">{request.sessionType}</span>
                  <span className="reschedule-request-by">{getRequestByMessage(request)}</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RescheduledRequestList;
